// FT-PRICING-UI: account page. Shows the user's plan and usage; pro
// users get a "Manage billing" button (Dodo Customer Portal); free
// users get an UpgradePrompt.

import { useEffect, useState } from "preact/hooks";
import { UpgradePrompt } from "./UpgradePrompt";
import { UserMenu } from "./UserMenu";
import { Footer } from "./Footer";
import { useNavigate } from "../contexts/AppContext";

type BillingMe = {
  plan: "free" | "pro";
  status: string;
  cancel_at_period_end: boolean;
  current_period_end: string | null;
  usage: {
    chat_turns: { pct: number; period_end: string | null };
  };
};

export function Account() {
  const navigate = useNavigate();
  const [me, setMe] = useState<BillingMe | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    fetch("/api/billing/me")
      .then((r) => r.json())
      .then(setMe)
      .catch(() => {});
  }, []);

  if (!me) return <div class="container py-5 text-muted">Loading…</div>;

  const openPortal = async () => {
    setBusy(true);
    try {
      const res = await fetch("/api/billing/portal", { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        alert(data.error || "Couldn't open billing portal.");
        return;
      }
      window.location.href = data.redirect_url;
    } finally {
      setBusy(false);
    }
  };

  const turns = me.usage.chat_turns;
  // Percentage only — we never surface the raw used/limit counts (the
  // server doesn't even send them). "N / 250" makes users ration prompts.
  const pct = Math.min(100, Math.max(0, turns.pct));

  return (
    <div class="d-flex flex-column min-vh-100">
      <div class="container py-5 flex-grow-1" style="max-width: 720px">
        <div class="d-flex flex-column gap-3 mb-4">
          <div class="d-flex align-items-center gap-3">
            <button
              type="button"
              class="btn btn-link p-0 text-decoration-none"
              onClick={() =>
                // history.state is non-null once the SPA router has pushed/replaced
                // an entry, so this returns to wherever Account was opened from
                // (editor, analytics, etc). Null only on a fresh load of /account.
                window.history.state === null
                  ? navigate("/")
                  : window.history.back()
              }
            >
              ← Back
            </button>
            <h1 class="h4 m-0">Account</h1>
          </div>
          <UserMenu showAccount={false} />
        </div>

        {me.plan === "pro" ? (
          <div class="card mb-3 plan-card-pro">
            <div class="card-body">
              <Sparkles />
              <div class="plan-pro-head">
                <span class="plan-crown-wrap">
                  <svg
                    class="plan-crown"
                    width="34"
                    height="34"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path d="M2 18l1.6-9.5 5 4.2L12 4l3.4 8.7 5-4.2L22 18H2zm0 2h20v2H2v-2z" />
                  </svg>
                </span>
                <div>
                  <div class="small text-uppercase plan-label">Your plan</div>
                  <div class="plan-name-pro">Pro</div>
                </div>
              </div>
              <div class="plan-pro-perk">
                You're a Pro — every feature unlocked. Thanks for backing
                MyPortal.
              </div>
              {me.cancel_at_period_end && me.current_period_end && (
                <div class="plan-pro-perk" style="color: #ffd166">
                  Cancellation scheduled — Pro continues through{" "}
                  {fmtDate(me.current_period_end)}.
                </div>
              )}
            </div>
          </div>
        ) : (
          <div class="card mb-3">
            <div class="card-body">
              <div class="text-muted small text-uppercase mb-1">Plan</div>
              <div class="h5 mb-0">Free</div>
            </div>
          </div>
        )}

        <div class="card mb-4">
          <div class="card-body">
            <div class="d-flex justify-content-between mb-2">
              <span class="text-muted small text-uppercase">Chat usage</span>
              <span class="small">{pct}% used</span>
            </div>
            <div class="progress" style="height: 8px">
              <div class="progress-bar" style={`width: ${pct}%`}></div>
            </div>
            {turns.period_end && (
              <div class="small text-muted mt-1">
                Resets {fmtDate(turns.period_end)}
              </div>
            )}
          </div>
        </div>

        {me.plan === "pro" ? (
          <button
            type="button"
            class="btn btn-outline-secondary"
            onClick={openPortal}
            disabled={busy}
          >
            {busy ? "Opening…" : "Manage billing"}
          </button>
        ) : (
          <UpgradePrompt reason="Pick up Pro to lift the chat-turn cap and unlock paid features." />
        )}
      </div>
      <Footer />
    </div>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString();
}

// Scattered twinkling sparkles for the Pro card. Positions and animation
// delays are spread by hand so they shimmer out of sync.
function Sparkles() {
  const sparks = [
    { top: "14%", left: "48%", size: 12, delay: "0s" },
    { top: "62%", left: "38%", size: 9, delay: "1.1s" },
    { top: "28%", left: "82%", size: 14, delay: "0.5s" },
    { top: "72%", left: "70%", size: 10, delay: "1.8s" },
    { top: "44%", left: "92%", size: 8, delay: "2.4s" },
  ];
  return (
    <>
      {sparks.map((s, i) => (
        <svg
          key={i}
          class="plan-spark"
          style={`top:${s.top};left:${s.left};animation-delay:${s.delay}`}
          width={s.size}
          height={s.size}
          viewBox="0 0 24 24"
          fill="currentColor"
          aria-hidden="true"
        >
          <path d="M12 0c.6 6.3 5.7 11.4 12 12-6.3.6-11.4 5.7-12 12-.6-6.3-5.7-11.4-12-12C6.3 11.4 11.4 6.3 12 0z" />
        </svg>
      ))}
    </>
  );
}
